import { Calendar, Cpu, ExternalLink } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTodaysTechTip } from "@/hooks/useTechTips";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { TipContent } from "@/components/TipContent";
import { generateTLDR } from "@/utils/generateTLDR";
// Helper function to get unique tech image
const getUniqueTechImage = (category?: string, id?: string) => {
  const categoryLower = category?.toLowerCase() || '';
  
  // Create a more unique hash from the ID to ensure different images
  let hash = 0;
  if (id) {
    for (let i = 0; i < id.length; i++) {
      const char = id.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
  }
  const imageVariant = Math.abs(hash) % 6; // Use 6 images for more variation
  
  if (categoryLower.includes('ai') || categoryLower.includes('artificial')) {
    const aiImages = [
      'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=600&h=400&fit=crop'
    ];
    return aiImages[imageVariant];
  }
  if (categoryLower.includes('mobile') || categoryLower.includes('app')) {
    const mobileImages = [
      'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1556656793-08538906a9f8?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1573152958734-1922c188fba3?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=600&h=400&fit=crop'
    ];
    return mobileImages[imageVariant];
  }
  if (categoryLower.includes('security') || categoryLower.includes('privacy')) {
    const securityImages = [
      'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop'
    ];
    return securityImages[imageVariant];
  }
  if (categoryLower.includes('productivity') || categoryLower.includes('tool')) {
    const productivityImages = [
      'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1542626991-cbc4e32524cc?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1517180102446-f3ece451e9d8?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=400&fit=crop'
    ];
    return productivityImages[imageVariant];
  }
  if (categoryLower.includes('browser') || categoryLower.includes('web')) {
    const webImages = [
      'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1593720219276-0b1eacd0aef4?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1547658719-da2b51169166?w=600&h=400&fit=crop',
      'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=600&h=400&fit=crop'
    ];
    return webImages[imageVariant];
  }
  
  // Default tech images with variation
  const defaultImages = [
    'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&h=400&fit=crop'
  ];
  return defaultImages[imageVariant];
};

export const TodaysTechTip = () => {
  const { data: techTip, isLoading } = useTodaysTechTip();
  
  console.log('TodaysTechTip component - techTip data:', techTip);
  console.log('TodaysTechTip component - image_url:', techTip?.image_url);

  if (isLoading) {
    return (
      <Card className="bg-gradient-to-br from-blue-50/50 to-cyan-50/50 dark:from-blue-950/20 dark:to-cyan-950/20 border-blue-200/50 dark:border-blue-800/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Cpu className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <CardTitle className="text-blue-900 dark:text-blue-100">Today's Tech Tip</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-blue-200 dark:bg-blue-800 rounded w-3/4"></div>
            <div className="h-4 bg-blue-200 dark:bg-blue-800 rounded w-full"></div>
            <div className="h-4 bg-blue-200 dark:bg-blue-800 rounded w-5/6"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!techTip) {
    return (
      <Card className="bg-gradient-to-br from-blue-50/50 to-cyan-50/50 dark:from-blue-950/20 dark:to-cyan-950/20 border-blue-200/50 dark:border-blue-800/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Cpu className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <CardTitle className="text-blue-900 dark:text-blue-100">Today's Tech Tip</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-blue-700 dark:text-blue-300">
            No tech tip available for today. Check back soon for the latest tech updates!
          </CardDescription>
        </CardContent>
      </Card>
    );
  }

  return (
    <Link to={`/tech-tips/${techTip.id}`} className="block">
      <Card className="bg-gradient-to-br from-blue-50/50 to-cyan-50/50 dark:from-blue-950/20 dark:to-cyan-950/20 border-blue-200/50 dark:border-blue-800/30 hover:shadow-lg transition-all duration-300 cursor-pointer group">
        <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Cpu className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <CardTitle className="text-blue-900 dark:text-blue-100">Today's Tech Tip</CardTitle>
          </div>
          <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
            <Calendar className="h-4 w-4" />
            {format(new Date(techTip.created_at), 'MMM d, yyyy')}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {techTip.category}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-100 mb-2 group-hover:text-blue-700 dark:group-hover:text-blue-200 transition-colors">
            {techTip.title}
          </h3>
          
          {/* TLDR Section */}
          <div className="bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200/50 dark:border-blue-800/30 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                🚀 TLDR
              </span>
            </div>
            <p className="text-blue-800 dark:text-blue-200 text-sm font-medium leading-relaxed">
              {generateTLDR(techTip.content)}
            </p>
          </div>
        </div>
        
        <div className="rounded-lg overflow-hidden bg-gradient-secondary">
          <img 
            src={techTip.image_url || getUniqueTechImage(techTip.category, techTip.id)} 
            alt={techTip.title}
            className="w-full h-48 object-cover"
            loading="lazy"
            onError={(e) => {
              console.error('Image failed to load, using fallback');
              const target = e.target as HTMLImageElement;
              target.src = 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=600&h=400&fit=crop';
            }}
          />
        </div>
        
        {techTip.video_url && (
          <div className="flex justify-center">
            <Button variant="outline" size="sm" className="text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-950/30">
              <ExternalLink className="h-4 w-4 mr-2" />
              Watch Video
            </Button>
          </div>
        )}
        
        <div className="flex justify-between items-center pt-2">
          <Badge variant="outline" className="text-xs text-blue-600 border-blue-300 dark:text-blue-400 dark:border-blue-700">
            Click to read more
          </Badge>
          <div className="text-xs text-blue-500 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
            Full details →
          </div>
        </div>
      </CardContent>
    </Card>
    </Link>
  );
};